import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { I18nString, PermitCategory } from '../../../models/permit-category.model';
import { PermitTypeConfig, permitTypeSubtitle } from '../../../models/permit-type.model';

@Component({
    selector: 'app-permit-type-step',
    templateUrl: './permit-type-step.component.html',
    styleUrls: ['./permit-type-step.component.scss'],
})
export class PermitTypeStepComponent implements OnInit, OnChanges, OnDestroy {
    @Input() selectedCategory: PermitCategory | null = null;

    @Input() types: PermitTypeConfig[] = [];

    @Input() loading = false;

    @Input() selectedType: PermitTypeConfig | null = null;

    @Output() typeSelected = new EventEmitter<PermitTypeConfig>();

    searchQuery = '';

    filteredTypes: PermitTypeConfig[] = [];

    private searchSubject = new Subject<string>();

    private searchSubscription: Subscription;

    constructor(private translate: TranslateService) {}

    ngOnInit(): void {
        this.searchSubscription = this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((query) => this.filterTypes(query));
        this.filterTypes(this.searchQuery);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.types) {
            this.filterTypes(this.searchQuery);
        }
    }

    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
    }

    localizedName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    typeSubtitle(type: PermitTypeConfig): string {
        return permitTypeSubtitle(type, this.translate.currentLang);
    }

    onSearchChange(): void {
        this.searchSubject.next(this.searchQuery);
    }

    isSelected(type: PermitTypeConfig): boolean {
        return this.selectedType?._id === type._id;
    }

    /** Short fee hint on the card: amount for fixed fees, a plain "fees apply" note otherwise. */
    feeHint(type: PermitTypeConfig): string {
        const fee = type.feeConfig;
        if (!fee) return '';
        if (fee.type === 'fixed' && fee.amount != null) {
            return `${fee.amount} $`;
        }
        return this.translate.instant('permit.create.permit_type.fee_applies');
    }

    selectType(type: PermitTypeConfig): void {
        this.typeSelected.emit(type);
    }

    private filterTypes(query: string): void {
        const term = (query || '').toLowerCase().trim();
        if (!term) {
            this.filteredTypes = [...(this.types || [])];
            return;
        }
        this.filteredTypes = (this.types || []).filter((type) => this.localizedName(type.name).toLowerCase().includes(term));
    }
}
